// Visit.js
// This is the Visit Details view.

import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { ActivityIndicator, Image, View, Text } from "react-native";
import { faNoteSticky } from "@fortawesome/free-regular-svg-icons";
import { faTriangleExclamation, faPaw, faFile, faDownload, faUpRightFromSquare, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

import Anchor from "components/Partials/Anchor";
import { stateReducer } from "components/helpers/stateReducer";
import { FontAwesomeIcon } from "@fortawesome/react-native-fontawesome";
import { openSideScreen } from "components/Views/SideScreens/SideScreenLoader";

import { PatientProfile, PatientVaccinations, PatientReminders, PatientPrescriptions } from "components/Views/SideScreens/Partials";


const visitDetails = ({client_id, patient_id, visit_id, patientObj={}}) => {
  const _theme = window.main.theme;

  if (!Number(client_id) || !Number(patient_id) || !Number(visit_id)) {
    return (
      <View id="visit_details_invalid" style={{..._theme.tiles.outerContainer,alignItems:"center",paddingTop:50}}>
        <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.caution,fontSize:18,lineHeight:22}}>Visit Details: Invalid visit.</Text>
      </View>
    );
  }
  const userProfile = window.main.lib.getUserProfile();

  const [visitViewState, visitViewSetState] = useReducer(stateReducer, {
    visitDetails: {},
    patientDetails: patientObj,
    visitLoading: true,  // ⬅ Default TRUE for iniital 'loading' state.
  });
  const _visitDetailsId = useRef(0);

  const initialDataLoad = useCallback(async () => {
    if (!!Number(_visitDetailsId.current)) {
      return false;
    }
    _visitDetailsId.current = visit_id;
    const _stateProps = [
      { key: "visitLoading", type: "boolean", visitLoading: false }
    ];
    try {
      // get core data and begin showing it, then get additional data and complete 'loading'.
      const _visitDetails = await window.main.axios.get(window.main.api.getVisitDetails.replace("{visit_id}",visit_id));
      if (!_visitDetails || !_visitDetails.data || !Number(_visitDetails.data.id)) {
        throw _visitDetails;
      }
      _stateProps.push({ key: "visitDetails", type: "object", visitDetails: _visitDetails.data });  // ⬅ Publish immediately for user visuals.
      if (!visitViewState.patientDetails || !Number(visitViewState.patientDetails.id)) {
        const _patientDetails = await window.main.axios.get(window.main.api.getPatientDetails.replace("{patient_id}",patient_id));
        if (!_patientDetails || !_patientDetails.data || !Number(_patientDetails.data.id)) {
          throw _patientDetails;
        }
        _stateProps.push({ key: "patientDetails", type: "object", patientDetails: _patientDetails.data });  // ⬅ Publish immediately for user visuals.
      }
      // Update all states at once:
      visitViewSetState(_stateProps);
    } catch (err) {
      const errMsg = window.main.lib.parseErrorMessage(err);
      window.main.lib.setStatusState("Error: "+errMsg);
    } finally {
      visitViewSetState(_stateProps);
    }
  }, []);

  const openClientSideScreen = ()=>{ openSideScreen("client", {client_id:visitViewState.visitDetails.client_id}); };
  const openPatientSideScreen = ()=>{ openSideScreen("patient", {client_id:visitViewState.visitDetails.client_id, patient_id:visitViewState.visitDetails.patient_id}); };

  useEffect(() => {
    initialDataLoad();
  }, []);  // ⬅ Trigger only once on initial page load.

  if (!visitViewState.visitLoading && !Number(visitViewState.visitDetails.id)) {
    return (
      <View id="visit_details_error" style={{..._theme.tiles.outerContainer,alignItems:"center",paddingTop:50}}>
        <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.caution,fontSize:18,lineHeight:22}}>Visit Details failed to load.</Text>
      </View>
    );
  }

  return (
    <>

      {!!Number(visitViewState.visitDetails.id) &&
        <View id="visit_details__sidescreen" style={_theme.tiles.outerContainer}>

          {/* Visit - CROSS-LOCATION WANTING */}
          {visitViewState.visitDetails.location_id!==userProfile.current_location_id &&
            <View style={{flexDirection:"row",backgroundColor:_theme.colors.warnBackground2,borderColor:_theme.colors.warnDark2,borderRadius:4,borderStyle:"solid",borderWidth:1,marginBottom:-10,padding:15}}>
              <FontAwesomeIcon icon={faTriangleExclamation} style={{height:28,width:28,color:_theme.colors.warnDark2,marginRight:10,marginTop:-5}} />
              <Text style={{color:_theme.colors.mode.textColor3,fontSize:16,fontWeight:"600"}}>
                Warning! Cross-Location: You are in '{userProfile.current_location.shortname}', but this Visit is in '{visitViewState.visitDetails.location.shortname}'.
              </Text>
            </View>
          }

          {/* Visit Details */}
          <View id="visit_profile" style={_theme.tileRowNested}>
            <View style={_theme.tiles.seactionHeader}>
              <Text style={_theme.tiles.sectionHeaderKey}>Visit:</Text>
              <Text style={_theme.tiles.sectionHeaderTitle}>{visitViewState.visitDetails.title}</Text>
              <Text style={{color:_theme.colors.mode.textColor5,fontSize:14,padding:10}}><FontAwesomeIcon icon={faNoteSticky} style={{color:_theme.colors.mode.textColor5,height:14,width:14,marginBottom:-2}} />{visitViewState.visitDetails.notes}</Text>
            </View>
            <View id="visit_profile_fields" style={{..._theme.tiles.fieldsColumn}}>
              {/* Regular fields */}
              <View style={_theme.tiles.fieldRow}>
                <Text style={_theme.tiles.fieldRowLabel}>Appointment:</Text>
                <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row"}}><Text style={{whiteSpace:"pre-wrap",marginRight:8}}>'{visitViewState.visitDetails.appointment_title}'</Text><Text style={{whiteSpace:"nowrap"}}>on {window.main.lib.dateTimeFormat(visitViewState.visitDetails.appointment_start_date.replace(/Z$/,""))}</Text></Text>
              </View>
              <View style={_theme.tiles.fieldRow}>
                <Text style={_theme.tiles.fieldRowLabel}>Created</Text>
                <Text style={_theme.tiles.fieldRowValue}><Text style={{whiteSpace:"nowrap"}}>{window.main.lib.dateFormat(visitViewState.visitDetails.created_date.replace(/Z$/,""))}</Text>{"\n"}<Text style={{fontSize:14,whiteSpace:"nowrap"}}>({window.main.lib.sinceDate(visitViewState.visitDetails.created_date.replace(/Z$/,""))})</Text></Text>
              </View>
              <View style={_theme.tiles.fieldRow}>
                <Text style={_theme.tiles.fieldRowLabel}>Updated</Text>
                <Text style={_theme.tiles.fieldRowValue}><Text style={{whiteSpace:"nowrap"}}>{window.main.lib.dateTimeFormat(visitViewState.visitDetails.updated_date.replace(/Z$/,""))}</Text>{"\n"}<Text style={{fontSize:14,whiteSpace:"nowrap"}}>({window.main.lib.sinceDate(visitViewState.visitDetails.updated_date.replace(/Z$/,""))})</Text></Text>
              </View>

              <Anchor key={"patientObj_"+patientObj.id} id={"client_patient_row_"+patientObj.id} style={{..._theme.tiles.fieldRow,alignItems:"flex-start",alignSelf:"stretch"}} onPress={openPatientSideScreen}>
                <View style={{..._theme.containerRow,flexShrink:1,alignItems:"center",color:"inherit"}}>
                  {!!Number(patientObj.profile_image_id) &&
                    <Image source={{ uri:`${window.main.api.url}/api/documents/download/${visitViewState.visitDetails.patient_id}/${visitViewState.visitDetails.patient_profile_image_id}` }} style={{height:34,width:34,objectFit:"cover",borderRadius:"50%",marginRight:10}} />
                  }
                  {!Number(patientObj.profile_image_id) &&
                    <FontAwesomeIcon icon={faPaw} style={{color:_theme.colors.mode.containerBackgroundColor4,height:24,width:24,minWidth:24,padding:2,borderWidth:3,borderColor:_theme.colors.mode.containerBackgroundColor4,borderRadius:"50%",borderStyle:"solid",marginTop:-2,marginBottom:-2,marginRight:10}} />
                  }
                  <Text style={{display:"inline-block",maxWidth:"65%",fontSize:16,color:"inherit"}}>
                    {visitViewState.visitDetails.patient_title}
                  </Text>
                </View>
              </Anchor>

              {/* <Anchor id="client_link_row" style={{..._theme.tiles.fieldRow,alignItems:"center",alignSelf:"stretch"}} onPress={openClientSideScreen}>
                <View style={{..._theme.containerRow,flexShrink:1,alignItems:"center",color:"inherit"}}>
                  {!!Number(visitViewState.visitDetails.client_profile_image_id) &&
                    <Image source={{ uri:`${window.main.api.url}/api/documents/download/${visitViewState.visitDetails.client_id}/${visitViewState.visitDetails.client_profile_image_id}` }} style={{height:34,width:34,objectFit:"cover",borderRadius:"50%",marginRight:10}} />
                  }
                  {!Number(visitViewState.visitDetails.client_profile_image_id) &&
                    <FontAwesomeIcon icon={faCircleUser} style={{color:_theme.colors.mode.containerBackgroundColor4,height:38,width:38,minWidth:38,marginTop:-4,marginBottom:-4,marginLeft:-2,marginRight:10}} />
                  }
                  <Text style={{..._theme.tiles.fieldRowLabel,flexBasis:"none",fontSize:16,color:"inherit"}}>
                    Client: {visitViewState.visitDetails.client_title}
                  </Text>
                </View>
              </Anchor> */}

            </View>
          </View>

          {/* Patient Profile */}
          <PatientProfile patientObj={visitViewState.patientDetails} />


          {/* Documents */}
          <View id="visit_documents" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Documents</Text>
            {!!visitViewState.visitDetails.documents && !!visitViewState.visitDetails.documents.length && visitViewState.visitDetails.documents.map((docObj)=>{
              return (
                <View key={"docObj_"+docObj.id} id={"patient_reminder_row_"+docObj.reminder_id} style={{..._theme.tiles.sectionRow,flexDirection:"row",justifyContent:"stretch"}}>
                  <View style={{..._theme.containerColumn,flexShrink:0,flexBasis:0}}>
                    <Text style={{..._theme.tiles.fieldRowLabel,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:16}}>
                      <FontAwesomeIcon icon={faFile} style={{color:_theme.colors.mode.textColor4,height:16,width:16,paddingTop:1,marginRight:8}} />
                      {docObj.title}
                    </Text>
                    {!!docObj.filename &&
                      <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row",marginTop:8,wordBreak:"break-all"}}>
                        {docObj.filename}
                      </Text>
                    }
                    <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row",marginTop:8}}>
                      Created: {window.main.lib.dateTimeFormat(docObj.created_date.replace(/Z$/,""))}
                    </Text>
                  </View>
                  <View style={{..._theme.containerColumn,alignItems:"flex-end",flexGrow:0,flexBasis:100,textAlign:"right"}}>
                    {!!docObj.filename &&
                      <Anchor href={`/api/documents/download/${docObj.client_id}/${docObj.id}`} force={true} style={{fontSize:14}}><FontAwesomeIcon icon={faDownload} style={{color:_theme.colors.baseDark,height:15,width:15,marginBottom:-2,marginRight:6}} />Download</Anchor>
                    }
                    {!!docObj.form_id &&
                      <Anchor href={`/api/forms/generate/${docObj.client_id}/${docObj.id}`} force={true} style={{fontSize:14}}><FontAwesomeIcon icon={faUpRightFromSquare} style={{color:_theme.colors.baseDark,height:15,width:15,marginBottom:-2,marginRight:6}} />View PDF</Anchor>
                    }
                  </View>
                </View>
              );
            })}
            {((!visitViewState.visitDetails.documents || !visitViewState.visitDetails.documents.length)) &&
              <View id="patient_reminder_row" style={_theme.tiles.sectionRow}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No reminders.</Text>
              </View>
            }
          </View>

          {/* Current Vaccinations */}
          <PatientVaccinations patient_id={visitViewState.visitDetails.patient_id} />

          {/* Current Reminders */}
          <PatientReminders patient_id={visitViewState.visitDetails.patient_id} />

          {/* Visit Services (Treament Plans) */}
          <View id="visit_Services" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Treatment Plans</Text>
            {!!visitViewState.visitDetails.services && !!visitViewState.visitDetails.services.length && visitViewState.visitDetails.services.map((serviceObj)=>{
              return (
                <View key={"serviceObj_"+serviceObj.service_id} id={"patient_Service_row_"+serviceObj.service_id} style={{..._theme.tiles.sectionRow,flexDirection:"row",justifyContent:"stretch"}}>
                  <View style={{..._theme.containerColumn,justifyContent:"center",flexShrink:0,flexBasis:0}}>
                    <Text style={{..._theme.tiles.fieldRowLabel,alignItems:"center",flexBasis:"none",flexGrow:1,flexShrink:0,fontSize:16}}>
                      <Text style={{..._theme.tileChip,marginRight:10}}>{serviceObj.quantity}</Text>
                      {serviceObj.title}
                    </Text>
                  </View>
                  {!!serviceObj.invoice_id &&
                    <View style={{..._theme.containerColumn,alignItems:"flex-end",flexGrow:0,flexBasis:100,textAlign:"right"}}>
                      <Text style={{..._theme.tiles.fieldRowLabel,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:13,marginRight:0}}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{color:_theme.colors.mode.textColor5,height:14,width:14,paddingTop:2,marginRight:6}} />
                        Invoiced
                      </Text>
                      <Text style={{..._theme.tiles.fieldRowLabel,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:13,marginRight:0}}>
                        ({serviceObj.invoice_id})
                      </Text>
                    </View>
                  }
                  {!serviceObj.invoice_id &&
                    <View style={{..._theme.containerColumn,alignItems:"flex-end",flexGrow:0,flexBasis:100,textAlign:"right"}}>
                      <Text style={{..._theme.tiles.fieldRowLabel,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:13,marginRight:0,color:_theme.colors.warning}}>
                        [Not invoiced]
                      </Text>
                    </View>
                  }
                </View>
              );
            })}
            {((!visitViewState.visitDetails.services || !visitViewState.visitDetails.services.length)) &&
              <View id="patient_service_row" style={_theme.tiles.sectionRow}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No reminders.</Text>
              </View>
            }
          </View>

          {/* Recent Prescriptions */}
          <PatientPrescriptions patient_id={visitViewState.visitDetails.patient_id} />

          {/* Visit Assessments */}
          <VisitAssessments visit_id={visitViewState.visitDetails.id} />

        </View>
      }


      {!window.main.lib.getLoadingState() && !!visitViewState.visitLoading &&
        // <View style={{zIndex:99999, backgroundColor:"rgba(255,255,255,0.7)", top:0, left:0, bottom:0, right:0, position:(window.main.platform_os==="web"?"fixed":"absolute")}}>
        <View style={{zIndex:99999,backgroundColor:"rgba(255,255,255,0.7)",top:0,left:0,bottom:0,right:0,position:"absolute"}}>{/* 'fixed' can't work within the sidescreen overlay. */}
          <ActivityIndicator size="large" color={_theme.colors.mode.base} style={{marginTop:20}} />
        </View>
      }

    </>
  );

};



//  Partials

const VisitAssessments = ({visit_id})=>{
  const _theme = window.main.theme;
  const [partialState, partialSetState] = useReducer(stateReducer, {
    assessments: [],
    assessmentsLoading: true,
  });
  const assessmentsDataLoad = useCallback(async () => {
    const _stateProps = [{key:"assessmentsLoading", type:"boolean", assessmentsLoading:false}];
    try {
      // Now get additional data
      const _patientVaccs = await window.main.axios.get(window.main.api.getVisitAssessments.replace("{visit_id}",visit_id));
      if (!!_patientVaccs && !!_patientVaccs.data ) {
      _stateProps.push({ key: "assessments", type: "array", assessments: _patientVaccs.data });
      }
      // Update all states at once:
      partialSetState(_stateProps);
    } catch (err) {
      console.log("assessments > Error:", err);
      const errMsg = window.main.lib.parseErrorMessage(err);
      window.main.lib.setStatusState("Error: "+errMsg);
    } finally {
      partialSetState(_stateProps);
    }
  }, []);
  useEffect(() => {
    assessmentsDataLoad();
  }, []);  // ⬅ Trigger only once on initial page load.
  return (
    <View id="visit_assessments" style={_theme.tileRowNested}>
      <Text style={_theme.tiles.sectionHeaderTitle}>Assessments</Text>
      {!!partialState.assessments && !!partialState.assessments.length && partialState.assessments.map((assObj)=>{
        let is_sig_needed = false;
        if (!!assObj.signatures && assObj.signatures.length) {
          for (const sigRow of assObj.signatures) {
            if (!!Number(sigRow.is_required && !Number(sigRow.signed))) {
              is_sig_needed = true;
              break;
            }
          }
        }
        return (
          <View  key={"assObj_"+assObj.assessment.id} id={"visit_assessment_row_"+assObj.assessment.id} style={_theme.tiles.sectionRow}>
            <Text style={{..._theme.tiles.fieldRowLabel,flexGrow:1,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:16}}>
              <FontAwesomeIcon icon={faFile} style={{color:_theme.colors.mode.textColor4,height:16,width:16,paddingTop:1,marginRight:8}} />
              {assObj.assessment.name}
              {!!is_sig_needed && <FontAwesomeIcon icon={faTriangleExclamation} style={{color:_theme.colors.caution,height:14,width:14,marginLeft:5}} />}
              {assObj.assessment.deleted_date===1 && <Text style={{color:_theme.colors.caution,marginLeft:10}}>[DELETED]</Text>}
            </Text>
            <Text style={{..._theme.tiles.fieldRowValue,flexGrow:1,flexDirection:"row",marginTop:8}}>
              <Text style={_theme.tiles.fieldRowValue}>{window.main.lib.dateTimeFormat(assObj.assessment.created_date.replace(/Z$/,""))}</Text>
            </Text>
          </View>
        );
      })}
      {((!partialState.assessments || !partialState.assessments.length) && !partialState.assessmentsLoading) &&
        <View id="visit_assessment_row" style={_theme.tiles.sectionRow}>
          <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No assessments.</Text>
        </View>
      }
      {!window.main.lib.getLoadingState() && (!partialState.assessments || !partialState.assessments.length) && !!partialState.assessmentsLoading &&
        <View id="visit_assessments_loading" style={{..._theme.tiles.sectionRow,minHeight:50}}>
          <View style={{zIndex:99999,backgroundColor:"rgba(255,255,255,0.7)",top:0,left:0,bottom:0,right:0,position:"absolute"}}>
            <ActivityIndicator size="large" color={_theme.colors.mode.base} style={{marginTop:10}} />
          </View>
        </View>
      }
    </View>
  );
};


export default visitDetails;
export const config = {
  key: "visit",
  title: "Visit Details",
  view: visitDetails,
  iconSource: "fa",
  icon: faPaw,
};

